import * as React from 'react';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import type {DateRange as DayPickerDateRange} from 'react-day-picker';

export type DateRange = [string, string] | string;

dayjs.extend(quarterOfYear);
dayjs.extend(utc);
dayjs.extend(tz);

/**
 * Returns the UTC instant for the start or end of a calendar day in the given
 * site-localization timezone.
 *
 * `day` carries a calendar date and nothing else — only its year/month/day are
 * read — so the conversion below is the single point where the site's real
 * offset *for that date* gets attached. Callers pick the date first (see
 * `siteCalendarToday`) and let this close it exactly once.
 *
 * When `timezoneFix` is true (WFA-2202), this returns the *true* UTC instant of
 * the local day boundary. For example, "Today" in `Pacific/Kiritimati` (UTC+14)
 * on June 12 produces
 *   start → 2026-06-11T10:00:00.000Z  (June 12 00:00:00 KIT)
 *   end   → 2026-06-12T09:59:59.999Z  (June 12 23:59:59.999 KIT)
 *
 * Callers must also forward the localization timezone to the analytics server
 * (`timeZone` param) so that downstream Druid queries bucket data by the same
 * day boundaries; otherwise UTC-day bucketing will split the local day across
 * two buckets.
 *
 * When `timezoneFix` is false (the default — flag off), it falls back to the
 * legacy behavior of stamping the local calendar day onto a UTC midnight, which
 * drops the timezone offset.
 */
const toUTCDayBoundary = (
  day: dayjs.Dayjs,
  boundary: 'start' | 'end',
  timezone: string,
  timezoneFix = false
): Date => {
  if (timezoneFix && day.isValid()) {
    const wallTime = boundary === 'start' ? '00:00:00.000' : '23:59:59.999';
    const stamped = dayjs.tz(
      `${day.format('YYYY-MM-DD')} ${wallTime}`,
      timezone
    );
    if (stamped.isValid()) {
      return stamped.toDate();
    }
  }
  // Belt-and-braces for a caller passing an invalid day: degrade to today's
  // UTC calendar date rather than propagate an Invalid Date, whose toISOString
  // the caller would reject with the RangeError this guard prevents
  // (WFA-2627). siteCalendarToday already degrades before reaching here.
  const safeDay = day.isValid() ? day : dayjs.utc();
  return boundary === 'start'
    ? safeDay.startOf('day').toDate()
    : safeDay.endOf('day').toDate();
};

// Warned at most once per session: one warn is enough to know the environment
// cannot produce the site calendar date.
let warnedInvalidSiteCalendarDay = false;

/**
 * The site's current calendar date, held as a plain UTC date so that the day
 * arithmetic callers layer on top is calendar arithmetic.
 *
 * `subtract`/`startOf`/`endOf` on a `.tz()` instance shift elapsed time rather
 * than calendar fields, so a span that crosses a DST change lands an hour off
 * local midnight, and on a zero-offset site (`.tz('UTC')`, which dayjs hands
 * back as a UTC-mode instance) `endOf('day')` advances the day fields to
 * tomorrow while the instant stays correct. Neither can happen here: this date
 * has no offset to lose and no DST to cross.
 *
 * Read straight from Intl rather than `dayjs().tz(timezone)`: dayjs's tz
 * instance path round-trips `new Date(date.toLocaleString(...))` internally and
 * returns an Invalid instance (no throw) whenever the browser cannot parse its
 * own localized output — Chrome 153 did exactly that, turning every preset into
 * "Invalid Date" and crashing the designer (WFA-2627). `formatToParts` has no
 * string parsing that can fail.
 */
const siteCalendarToday = (timezone: string) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  const candidate = dayjs.utc(
    `${part('year')}-${part('month')}-${part('day')}`
  );
  if (candidate.isValid()) {
    return candidate;
  }
  if (!warnedInvalidSiteCalendarDay) {
    warnedInvalidSiteCalendarDay = true;
    console.warn(
      '[datepicker] could not read the site calendar date; preset boundaries degrade to the UTC calendar date (invalidSiteCalendarDay)'
    );
  }
  // Degrade to the UTC calendar date so callers' day arithmetic still spans
  // 7/30/90 days — only the day anchor changes, not the preset shapes.
  return dayjs.utc().startOf('day');
};

export const getInitialDateRange = (timezone: string): [Date, Date] => {
  const today = siteCalendarToday(timezone);
  return [
    toUTCDayBoundary(today.subtract(6, 'days'), 'start', timezone, true),
    toUTCDayBoundary(today, 'end', timezone, true),
  ];
};

export const timezoneDateRangeCollection = (
  timezone: string,
  timezoneFix = false
) => {
  const today = siteCalendarToday(timezone);
  const from = (day: dayjs.Dayjs) =>
    toUTCDayBoundary(day, 'start', timezone, timezoneFix);
  const to = (day: dayjs.Dayjs) =>
    toUTCDayBoundary(day, 'end', timezone, timezoneFix);

  return [
    {
      from: from(today),
      to: to(today),
      relativeTimeString: 'Today',
      id: 'today',
    },
    {
      from: from(today.subtract(1, 'day')),
      to: to(today.subtract(1, 'day')),
      relativeTimeString: 'Yesterday',
      id: 'yesterday',
    },
    {
      from: from(today.startOf('week')),
      // When the timezone fix is on, clamp the upper bound to today (end-of-day)
      // so "This week" never extends into future days for which we have no data.
      // Since today is always within the current week, today's end-of-day is
      // at or before `endOf('week')`, making the clamp safe and correct.
      to: to(timezoneFix ? today : today.endOf('week')),
      relativeTimeString: 'This week',
      id: 'thisWeek',
    },
    {
      from: from(today.subtract(1, 'week').startOf('week')),
      to: to(today.subtract(1, 'week').endOf('week')),
      relativeTimeString: 'Last week',
      id: 'lastWeek',
    },
    {
      from: from(today.subtract(6, 'days')),
      to: to(today),
      relativeTimeString: 'Last 7 days',
      id: 'last7Days',
    },
    {
      from: from(today.startOf('month')),
      // When the timezone fix is on, clamp the upper bound to today (end-of-day)
      // so "This month" never extends into future days for which we have no data.
      // Today's end-of-day is always at or before `endOf('month')`.
      to: to(timezoneFix ? today : today.endOf('month')),
      relativeTimeString: 'This month',
      id: 'thisMonth',
    },
    {
      from: from(today.subtract(1, 'month').startOf('month')),
      to: to(today.subtract(1, 'month').endOf('month')),
      relativeTimeString: 'Last month',
      id: 'lastMonth',
    },
    {
      from: from(today.subtract(29, 'days')),
      to: to(today),
      relativeTimeString: 'Last 30 days',
      id: 'last30Days',
    },
    {
      from: from(today.startOf('quarter')),
      // When the timezone fix is on, clamp the upper bound to today (end-of-day)
      // so "This quarter" never extends into future days for which we have no
      // data. Today's end-of-day is always at or before `endOf('quarter')`.
      to: to(timezoneFix ? today : today.endOf('quarter')),
      relativeTimeString: 'This quarter',
      id: 'thisQuarter',
    },
    {
      from: from(today.subtract(1, 'quarter').startOf('quarter')),
      to: to(today.subtract(1, 'quarter').endOf('quarter')),
      relativeTimeString: 'Last quarter',
      id: 'lastQuarter',
    },
    {
      from: from(today.subtract(89, 'days')),
      to: to(today),
      relativeTimeString: 'Last 90 days',
      id: 'last90Days',
    },
  ] as const;
};

export const dateRangeCollection = [
  {
    from: dayjs.utc().startOf('day').toDate(),
    to: dayjs.utc().endOf('day').toDate(),
    relativeTimeString: 'Today',
    id: 'today',
  },
  {
    from: dayjs.utc().subtract(1, 'day').startOf('day').toDate(),
    to: dayjs.utc().subtract(1, 'day').endOf('day').toDate(),
    relativeTimeString: 'Yesterday',
    id: 'yesterday',
  },
  {
    from: dayjs.utc().startOf('week').toDate(),
    to: dayjs.utc().endOf('week').toDate(),
    relativeTimeString: 'This week',
    id: 'thisWeek',
  },
  {
    from: dayjs.utc().subtract(1, 'week').startOf('week').toDate(),
    to: dayjs.utc().subtract(1, 'week').endOf('week').toDate(),
    relativeTimeString: 'Last week',
    id: 'lastWeek',
  },
  {
    from: dayjs().utc().startOf('day').subtract(6, 'days').toDate(),
    to: dayjs().utc().endOf('day').toDate(),
    relativeTimeString: 'Last 7 days',
    id: 'last7Days',
  },
  {
    from: dayjs.utc().startOf('month').toDate(),
    to: dayjs.utc().endOf('month').toDate(),
    relativeTimeString: 'This month',
    id: 'thisMonth',
  },
  {
    from: dayjs.utc().subtract(1, 'month').startOf('month').toDate(),
    to: dayjs.utc().subtract(1, 'month').endOf('month').toDate(),
    relativeTimeString: 'Last month',
    id: 'lastMonth',
  },
  {
    from: dayjs.utc().startOf('day').subtract(29, 'days').toDate(),
    to: dayjs.utc().endOf('day').toDate(),
    relativeTimeString: 'Last 30 days',
    id: 'last30Days',
  },
  {
    from: dayjs.utc().startOf('quarter').toDate(),
    to: dayjs.utc().endOf('quarter').toDate(),
    relativeTimeString: 'This quarter',
    id: 'thisQuarter',
  },
  {
    from: dayjs.utc().subtract(1, 'quarter').startOf('quarter').toDate(),
    to: dayjs.utc().subtract(1, 'quarter').endOf('quarter').toDate(),
    relativeTimeString: 'Last quarter',
    id: 'lastQuarter',
  },
  {
    from: dayjs.utc().startOf('day').subtract(89, 'days').toDate(),
    to: dayjs.utc().endOf('day').toDate(),
    relativeTimeString: 'Last 90 days',
    id: 'last90Days',
  },
  // Removing these for now as bandwidth data is not available before April 1st, 2024
  // {
  //   from: dayjs.utc().startOf('year').toDate(),
  //   to: dayjs.utc().endOf('year').toDate(),
  //   relativeTimeString: 'This year',
  //   id: 'thisYear',
  // },
  // {
  //   from: dayjs.utc().subtract(1, 'year').startOf('year').toDate(),
  //   to: dayjs.utc().subtract(1, 'year').endOf('year').toDate(),
  //   relativeTimeString: 'Last year',
  //   id: 'lastYear',
  // },
  // {
  //   from: dayjs.utc().subtract(12, 'months').toDate(),
  //   to: dayjs.utc().toDate(),
  //   relativeTimeString: 'Last 12 months',
  //   id: 'last12Months',
  // },
] as const;

export type UsagePeriodsDataPickerItemProps = DateRangeEntry & {
  id: string;
  endExclusive?: Date;
};

// Up to 3 billing-period entries shown above the standard ranges.
export type UsagePeriodsDataPickerProps =
  ReadonlyArray<UsagePeriodsDataPickerItemProps>;

export type DateRangeEntry = {
  from: Date;
  to: Date;
  relativeTimeString: string;
};

export type DateRangeProps = {
  today: DateRangeEntry;
  yesterday: DateRangeEntry;
  thisWeek: DateRangeEntry;
  lastWeek: DateRangeEntry;
  last7Days: DateRangeEntry;
  thisMonth: DateRangeEntry;
  lastMonth: DateRangeEntry;
  last30Days: DateRangeEntry;
  thisQuarter: DateRangeEntry;
  lastQuarter: DateRangeEntry;
  last90Days: DateRangeEntry;
  thisYear: DateRangeEntry;
  lastYear: DateRangeEntry;
  last12Months: DateRangeEntry;
};

export const createDateRange = (
  from: Date | string | number,
  to: Date | string | number,
  relativeTimeString?: string
): DateRangeEntry => ({
  from: dayjs.utc(from).toDate(),
  to: dayjs.utc(to).toDate(),
  relativeTimeString: relativeTimeString || 'Custom',
});

/**
 * Calculates difference (in minutes) between local timezone and the provided timezone.
 */
export const getTimezoneOffset = (timezone: string) => {
  const localNow = dayjs();
  const localOffset = localNow.utcOffset();
  const tzNow = dayjs().tz(timezone);
  const tzOffset = tzNow.utcOffset();
  const diffInMinutes = localOffset - tzOffset;
  return diffInMinutes;
};

export const dateRanges = dateRangeCollection.reduce((acc, item) => {
  return {
    ...acc,
    [item.id]: createDateRange(item.from, item.to, item.relativeTimeString),
  };
}, {}) as DateRangeProps;

export const timezoneDateRanges = (timezone: string, timezoneFix = false) =>
  timezoneDateRangeCollection(timezone, timezoneFix).reduce((acc, item) => {
    return {
      ...acc,
      [item.id]: createDateRange(item.from, item.to, item.relativeTimeString),
    };
  }, {}) as DateRangeProps;

export const findLabelForDateRange = (
  dateRange: DateRange,
  timezone?: string,
  timezoneFix = false
): string => {
  const toLocalDayKey = (d: Date | string) =>
    timezone
      ? dayjs(d).tz(timezone).format('YYYY-MM-DD')
      : dayjs.utc(d).format('YYYY-MM-DD');
  const dateRangeEntry = Object.values(
    timezone ? timezoneDateRanges(timezone, timezoneFix) : dateRanges
  ).find(
    (range) =>
      toLocalDayKey(range.from) === toLocalDayKey(dateRange[0]) &&
      toLocalDayKey(range.to) === toLocalDayKey(dateRange[1])
  );

  return dateRangeEntry?.relativeTimeString || 'Custom';
};

export const getBillingCycleDateRange = (
  startDate: Date | string | number,
  endDate?: Date | string | number
): DateRangeEntry[] => {
  const dateRange = [];
  const numberOfMonths = dayjs.utc(endDate).diff(startDate, 'months');

  /**
   * Fill in the number of months between the start and end date
   * with the billing cycle date range.
   */
  for (let i = 0; i < numberOfMonths; i++) {
    const cycleStartDate = dayjs.utc(startDate).add(i, 'month');
    const cycleEndDate = cycleStartDate.add(1, 'month').subtract(1, 'day');
    dateRange.push([cycleStartDate.toDate(), cycleEndDate.toDate()]);
  }

  /**
   * Handles the case where the current date is outside the last calculated
   * date range.
   */
  const lastEntry = dateRange[dateRange.length - 1];
  if (lastEntry && dayjs.utc(lastEntry[1]).isBefore(dayjs.utc())) {
    const cycleStartDate = dayjs.utc(lastEntry[1]).add(1, 'day');
    const cycleEndDate = cycleStartDate.add(1, 'month').subtract(1, 'day');
    dateRange.push([cycleStartDate.toDate(), cycleEndDate.toDate()]);
  }

  /**
   * Handles the case where the billing cycle just started.
   */
  const hasNoEntries = dateRange.length === 0;
  if (hasNoEntries) {
    const cycleStartDate = dayjs.utc(startDate);
    const cycleEndDate = cycleStartDate.add(1, 'month').subtract(1, 'day');
    dateRange.push([cycleStartDate.toDate(), cycleEndDate.toDate()]);
  }

  return (
    dateRange
      .map(([from, to]) => {
        if (!from || !to) {
          return;
        }

        const label = `${dayjs.utc(from).format('MMM DD YYYY')} - ${dayjs
          .utc(to)
          .format('MMM DD YYYY')}`;

        return createDateRange(from, to, label);
      })
      .filter(Boolean)
      /**
       * Order date ranges from current -> oldest.
       */
      .reverse() as DateRangeEntry[]
  );
};

export const formatUsagePeriodDate = (usagePeriodDate: Date) => {
  return usagePeriodDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const getUTCEquivalent = (date: Date) => {
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

export const CURRENT_BILLING_MONTH = 'Current billing month';
export const LAST_BILLING_MONTH = 'Last billing month';
export const PREVIOUS_BILLING_MONTH = 'Previous billing month';
export const BILLING_MONTH = 'Billing month';

/**
 * Custom hook for managing the goToDate state pattern used in DatePicker components.
 * This is a workaround to lift the `goToMonth` function from react-day-picker.
 */
export const useGoToDate = () => {
  const [goToDate, _setGoToDate] = React.useState<
    ((date: Date) => void) | null
  >(() => null);

  /**
   * Work-around.
   * Used to lift the `goToMonth` function from the react-day-picker library
   * to our DatePicker components.
   *
   * The react-day-picker `goToMonth` function is used to navigate to a
   * specific month. We use this to smoothen the (visual) transition when
   * selecting any date range.
   */
  const setGoToDate = (nextFn: (((date: Date) => void) | null) | null) => {
    // @ts-expect-error TS2345
    _setGoToDate((fn: ((date: Date) => void) | null) => {
      if (fn !== null) {
        return;
      }
      return nextFn;
    });
  };

  // Ensure goToDate is always a function (never null) for context compatibility
  const goToDateFn = goToDate ?? (() => null);

  return {goToDate: goToDateFn, setGoToDate};
};

/**
 * Offsets a single date for UTC display.
 *
 * While we use UTC times across DatePicker components, new Date objects are created in the browser's timezone.
 * This causes an issue where the DatePicker's displayed date doesn't match the date being used across components
 * nor the date actually sent to the server. We solve this by offsetting the date by the timezone offset, which
 * forces the DatePicker's visual representation to match what is truly being used in UTC.
 */
export const offsetDateForUTC = (date: Date, timezone?: string): Date => {
  const msMultiplier = 60 * 1000;
  if (timezone) {
    return new Date(
      date.getTime() - getTimezoneOffset(timezone) * msMultiplier
    );
  }
  return new Date(date.getTime() + date.getTimezoneOffset() * msMultiplier);
};

/**
 * Offsets a date range for UTC display.
 *
 * While we use UTC times across DatePicker components, new Date objects are created in the browser's timezone.
 * This causes an issue where the DatePicker's displayed date doesn't match the date being used across components
 * nor the date actually sent to the server. We solve this by offsetting the dates by the timezone offset, which
 * forces the DatePicker's visual representation to match what is truly being used in UTC. This utility will be used
 * to apply the above UTC offset to a DateRange.
 */
export const offsetRangeForUTC = (
  range: DayPickerDateRange | null,
  timezone?: string
): {from?: Date; to?: Date} | null => {
  if (!range) return null;

  const {from, to} = range;

  let utcFrom;
  const msMultiplier = 60 * 1000;
  if (from) {
    utcFrom = timezone
      ? new Date(from.getTime() - getTimezoneOffset(timezone) * msMultiplier)
      : new Date(from.getTime() + from.getTimezoneOffset() * msMultiplier);
  }

  let utcTo;
  if (to) {
    utcTo = timezone
      ? new Date(to.getTime() - getTimezoneOffset(timezone) * msMultiplier)
      : new Date(to.getTime() + to.getTimezoneOffset() * msMultiplier);
  }
  return {from: utcFrom, to: utcTo};
};
